import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import { PageHint } from '../../components/common/PageHint';
import { getExpertQuestionnaireById } from '../../services/questionnaires/questionnaire.service';
import { Questionnaire } from '../../types/questionnaire';
import { QuestionnaireViewer } from '../../services/questionnaires/questionnaireViewRenderer';
import {
  Box,
  CircularProgress,
  Typography,
  Alert,
} from '@mui/material';

interface ExpertClientQuestionnaireDetailProps {
  clientId: string;
  onTitleChange?: (name: string | null) => void;
}

export const ExpertClientQuestionnaireDetail: React.FC<ExpertClientQuestionnaireDetailProps> = ({ clientId, onTitleChange }) => {
  const { templateId } = useParams<{ templateId: string }>();
  const [questionnaire, setQuestionnaire] = useState<Questionnaire | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId || !templateId) return;
    const fetchQuestionnaire = async () => {
      try {
        setLoading(true);
        const data = await getExpertQuestionnaireById(clientId, templateId);
        if (data) {
          setQuestionnaire(data);
          onTitleChange?.(data.name);
        } else {
          setError('Не вдалося знайти опитувальник.');
        }
      } catch (err) {
        setError('Помилка при завантаженні даних.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestionnaire();

    return () => onTitleChange?.(null);
  }, [clientId, templateId]);

  if (loading && !questionnaire) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!questionnaire) {
    return null;
  }

  return (
    <Box data-i-doc={PageIndices.EXPERT_CLIENT_QUESTIONNAIRE}>
        <PageHint iDoc={PageIndices.EXPERT_CLIENT_QUESTIONNAIRE} />
        <Box sx={{ mb: 3 }}>
            <Typography variant="h5">{questionnaire.name}</Typography>
            <Typography variant="body1" color="text.secondary">
            {questionnaire.description}
            </Typography>
            {questionnaire.welcomeNotes && (
            <Alert severity="info" sx={{ mt: 2 }}>
                {questionnaire.welcomeNotes}
            </Alert>
            )}
        </Box>

        <QuestionnaireViewer questionnaire={questionnaire} />
    </Box>
  );
};
